'use client';

import { useState, useEffect } from 'react';
import { useParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { productService, type Product } from '@/lib/services/productService';
import { InventoryBadge, InventoryProvider } from '@/app/products/components/InventoryBadge';

const categoryLabels: Record<string, string> = {
  cpu: 'CPU 處理器',
  gpu: 'GPU 顯示卡',
  ram: 'RAM 記憶體',
  motherboard: '主機板 Motherboard',
  storage: 'Storage 儲存',
  cooling: 'Cooling 散熱',
  psu: 'PSU 電源供應器',
  case: 'Case 機殼',
  peripheral: 'Peripherals 週邊',
};

const stockConfig = {
  'in-stock': { label: 'In Stock', labelZh: '有貨', dot: 'bg-green-500', color: 'text-green-600', bg: 'bg-green-50 border-green-200' },
  'low-stock': { label: 'Low Stock', labelZh: '少量', dot: 'bg-yellow-500', color: 'text-yellow-600', bg: 'bg-yellow-50 border-yellow-200' },
  'out-of-stock': { label: 'Out of Stock', labelZh: '缺貨', dot: 'bg-red-500', color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
};

function parseSpecs(specs: string): { label: string; value: string }[] {
  return specs.split('/').map((s) => {
    const trimmed = s.trim();
    const colonIdx = trimmed.indexOf(':');
    if (colonIdx > -1) {
      return { label: trimmed.substring(0, colonIdx).trim(), value: trimmed.substring(colonIdx + 1).trim() };
    }
    // Try to detect common patterns like "24 Cores", "5.8GHz", "LGA1700"
    const match = trimmed.match(/^(\d[\d.]*\s*[A-Za-z]+|[A-Z]+\d+[\w]*|\d+[A-Za-z]+.*)$/);
    if (match) {
      return { label: '', value: trimmed };
    }
    return { label: '', value: trimmed };
  }).filter((s) => s.value);
}

function RelatedProductCard({ product }: { product: Product }) {
  const stockCfg = stockConfig[product.stockStatus];
  return (
    <Link href={`/products/${product.id}`} className="group block bg-white border border-gray-100 hover:border-orange/40 rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
      <div className="relative h-40 bg-bg-light overflow-hidden">
        <AppImage
          src={product.image}
          alt={product.alt}
          fill
          className="object-cover transition-transform duration-500 group-hover:scale-105"
          sizes="(max-width: 640px) 50vw, 25vw"
        />
        {product.badge && (
          <div className="absolute top-2 left-2">
            <span className={`text-xs font-black px-2.5 py-1 rounded-full ${product.badgeColor}`}>{product.badge}</span>
          </div>
        )}
      </div>
      <div className="p-4">
        <p className="text-xs font-bold text-orange uppercase tracking-wide mb-1">{product.brand}</p>
        <h4 className="font-heading font-black text-navy text-sm leading-tight mb-1 line-clamp-2">{product.name}</h4>
        <div className="flex items-center gap-1.5 mb-2">
          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${stockCfg.dot}`} />
          <span className={`text-xs font-semibold ${stockCfg.color}`}>{stockCfg.labelZh}</span>
        </div>
        <p className="text-xs font-mono text-text-mid">${product.priceRange[0]} – ${product.priceRange[1]} USD</p>
      </div>
    </Link>
  );
}

export default function ProductDetailPage() {
  const params = useParams();
  const router = useRouter();
  const productId = params?.id as string;

  const [product, setProduct] = useState<Product | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    if (!productId) return;
    async function load() {
      setLoading(true);
      try {
        const p = await productService.getById(productId);
        if (!p) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        setProduct(p);
        const related = await productService.getRelated(p.category, p.id, 4);
        setRelatedProducts(related);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [productId]);

  const whatsappMessage = product
    ? encodeURIComponent(`您好，我想詢問以下產品的報價：\n\n產品：${product.name}\n規格：${product.specs}\n${product.moq}\n\n請提供最新報價，謝謝！`)
    : '';

  const whatsappUrl = `https://wa.me/85259773386?text=${whatsappMessage}`;

  if (loading) {
    return (
      <main className="min-h-screen">
        <Header />
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-orange border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-text-muted font-semibold">載入中 Loading...</p>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  if (notFound || !product) {
    return (
      <main className="min-h-screen">
        <Header />
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <Icon name="ExclamationCircleIcon" size={64} className="text-border mx-auto mb-4" />
            <h2 className="font-heading font-black text-navy text-2xl mb-2">找不到產品</h2>
            <p className="text-text-muted mb-6">Product not found</p>
            <Link href="/products" className="inline-flex items-center gap-2 bg-orange text-white px-6 py-3 rounded-full font-bold hover:bg-orange-dark transition-colors">
              <Icon name="ArrowLeftIcon" size={16} />
              返回產品目錄
            </Link>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  const stockCfg = stockConfig[product.stockStatus];
  const specItems = parseSpecs(product.specs);
  // Use the main image plus a few placeholder angles for the image gallery
  const images = [product.image];

  return (
    <main className="min-h-screen bg-bg-light">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-3">
          <nav className="flex items-center gap-2 text-sm text-text-muted">
            <Link href="/homepage" className="hover:text-orange transition-colors">首頁</Link>
            <Icon name="ChevronRightIcon" size={14} className="text-border" />
            <Link href="/products" className="hover:text-orange transition-colors">產品目錄</Link>
            <Icon name="ChevronRightIcon" size={14} className="text-border" />
            <Link href={`/products?category=${product.category}`} className="hover:text-orange transition-colors">
              {categoryLabels[product.category] || product.category}
            </Link>
            <Icon name="ChevronRightIcon" size={14} className="text-border" />
            <span className="text-navy font-semibold truncate max-w-[200px]">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Main Product Section */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">

          {/* Left: Image Gallery */}
          <div>
            <div className="relative bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm aspect-square mb-4">
              <AppImage
                src={images[activeImageIndex]}
                alt={product.alt}
                fill
                className="object-contain p-6"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
              {product.badge && (
                <div className="absolute top-4 left-4">
                  <span className={`text-sm font-black px-4 py-1.5 rounded-full ${product.badgeColor}`}>{product.badge}</span>
                </div>
              )}
            </div>
            {/* Thumbnail row (single image for now) */}
            <div className="flex gap-3">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImageIndex(idx)}
                  className={`relative w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${
                    activeImageIndex === idx ? 'border-orange' : 'border-gray-200 hover:border-orange/40'
                  }`}
                >
                  <AppImage src={img} alt={`${product.name} view ${idx + 1}`} fill className="object-contain p-1" sizes="80px" />
                </button>
              ))}
            </div>
          </div>

          {/* Right: Product Info */}
          <div className="flex flex-col">
            {/* Brand + Category */}
            <div className="flex items-center gap-3 mb-3">
              <span className="text-sm font-black text-orange uppercase tracking-widest">{product.brand}</span>
              <span className="text-xs text-text-muted bg-bg-light px-3 py-1 rounded-full border border-gray-200">
                {categoryLabels[product.category] || product.category}
              </span>
            </div>

            {/* Name */}
            <h1 className="font-heading font-black text-navy text-3xl md:text-4xl leading-tight mb-1">
              {product.name}
            </h1>
            <p className="text-text-muted text-lg mb-5">{product.nameZh}</p>

            {/* Stock Status */}
            <InventoryProvider>
              {(inventoryMap, invLoading) => (
                <div className={`inline-flex items-center gap-3 px-4 py-3 rounded-2xl border mb-5 w-fit ${stockCfg.bg}`}>
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${stockCfg.dot} ${product.stockStatus === 'in-stock' ? '' : 'animate-pulse'}`} />
                  <span className={`font-bold text-sm ${stockCfg.color}`}>{stockCfg.label}</span>
                  <span className={`text-sm ${stockCfg.color}`}>{stockCfg.labelZh}</span>
                  {!invLoading && (
                    <InventoryBadge productId={product.id} inventoryMap={inventoryMap} />
                  )}
                </div>
              )}
            </InventoryProvider>

            {/* Price */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5">
              <p className="text-xs text-text-muted uppercase tracking-widest font-bold mb-1">批發價格 Wholesale Price</p>
              <div className="flex items-baseline gap-2">
                <span className="font-heading font-black text-navy text-3xl">
                  ${product.priceRange[0].toLocaleString()}
                </span>
                <span className="text-text-muted font-semibold text-lg">– ${product.priceRange[1].toLocaleString()} USD</span>
              </div>
              <p className="text-xs text-text-muted mt-1">* 價格因數量而異，請聯繫我們獲取最新報價</p>
            </div>

            {/* MOQ */}
            <div className="flex items-center gap-2 mb-5">
              <Icon name="CubeIcon" size={16} className="text-orange" />
              <span className="text-sm font-bold text-navy">{product.moq}</span>
              <span className="text-xs text-text-muted">· 可接受更大批量訂單</span>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#1ebe5d] text-white py-4 px-6 rounded-2xl font-black text-base transition-all shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-5 h-5">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                WhatsApp 立即詢價
              </a>
              <a
                href="/contact"
                className="flex items-center justify-center gap-2 border-2 border-navy text-navy hover:bg-navy hover:text-white py-4 px-6 rounded-2xl font-bold text-sm transition-all"
              >
                <Icon name="EnvelopeIcon" size={16} />
                發送詢價表單
              </a>
            </div>

            {/* Trust badges */}
            <div className="grid grid-cols-3 gap-3">
              <div className="flex flex-col items-center gap-1 bg-bg-light rounded-xl p-3 border border-gray-100">
                <Icon name="ShieldCheckIcon" size={20} className="text-orange" />
                <span className="text-xs font-bold text-navy text-center">正品保證</span>
              </div>
              <div className="flex flex-col items-center gap-1 bg-bg-light rounded-xl p-3 border border-gray-100">
                <Icon name="TruckIcon" size={20} className="text-orange" />
                <span className="text-xs font-bold text-navy text-center">快速出貨</span>
              </div>
              <div className="flex flex-col items-center gap-1 bg-bg-light rounded-xl p-3 border border-gray-100">
                <Icon name="ChatBubbleLeftRightIcon" size={20} className="text-orange" />
                <span className="text-xs font-bold text-navy text-center">24小時報價</span>
              </div>
            </div>
          </div>
        </div>

        {/* Specs Table */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 mb-10">
          <h2 className="font-heading font-black text-navy text-2xl mb-6 flex items-center gap-3">
            <Icon name="CpuChipIcon" size={24} className="text-orange" />
            產品規格 Specifications
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-gray-100">
            <div className="md:pr-8">
              {specItems.slice(0, Math.ceil(specItems.length / 2)).map((spec, idx) => (
                <div key={idx} className="flex items-start py-3.5 border-b border-gray-50 last:border-0">
                  <span className="text-sm text-text-muted w-2 mr-3 mt-0.5">·</span>
                  <span className="text-sm font-semibold text-navy flex-1">{spec.value}</span>
                </div>
              ))}
            </div>
            <div className="md:pl-8">
              {specItems.slice(Math.ceil(specItems.length / 2)).map((spec, idx) => (
                <div key={idx} className="flex items-start py-3.5 border-b border-gray-50 last:border-0">
                  <span className="text-sm text-text-muted w-2 mr-3 mt-0.5">·</span>
                  <span className="text-sm font-semibold text-navy flex-1">{spec.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Full specs string */}
          <div className="mt-6 bg-bg-light rounded-2xl p-4 border border-gray-100">
            <p className="text-xs text-text-muted uppercase tracking-widest font-bold mb-2">完整規格</p>
            <p className="font-mono text-sm text-navy leading-relaxed">{product.specs}</p>
          </div>
        </div>

        {/* B2B Info */}
        <div className="bg-navy rounded-3xl p-8 mb-10 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{ background: 'radial-gradient(circle at 80% 50%, #FF8C00, transparent 60%)' }} />
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="font-heading font-black text-white text-xl mb-2">批量採購優惠</h3>
              <p className="text-white/60 text-sm">數量越多，價格越優惠。歡迎提供BOM清單，我們為您提供整套方案報價。</p>
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <Icon name="CheckCircleIcon" size={18} className="text-orange flex-shrink-0" variant="solid" />
                <span className="text-white/80 text-sm">原廠授權正品，附保固</span>
              </div>
              <div className="flex items-center gap-3">
                <Icon name="CheckCircleIcon" size={18} className="text-orange flex-shrink-0" variant="solid" />
                <span className="text-white/80 text-sm">24小時報價，快速出貨</span>
              </div>
              <div className="flex items-center gap-3">
                <Icon name="CheckCircleIcon" size={18} className="text-orange flex-shrink-0" variant="solid" />
                <span className="text-white/80 text-sm">香港、台灣、全球配送</span>
              </div>
            </div>
            <div className="flex items-center justify-center md:justify-end">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 bg-orange hover:bg-orange-dark text-white px-7 py-4 rounded-2xl font-black text-sm transition-all shadow-orange-glow"
              >
                <Icon name="ChatBubbleLeftRightIcon" size={18} variant="solid" />
                立即詢價
              </a>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-black text-navy text-2xl">
                相關產品 Related Products
              </h2>
              <Link href={`/products?category=${product.category}`} className="text-orange font-bold text-sm hover:underline flex items-center gap-1">
                查看全部 <Icon name="ArrowRightIcon" size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {relatedProducts.map((rp) => (
                <RelatedProductCard key={rp.id} product={rp} />
              ))}
            </div>
          </div>
        )}
      </section>

      <Footer />
    </main>
  );
}
